import React from "react";
import { Link } from "react-router-dom";

// ── Award/Cert Badge Card ─────────────────────────────────────────────────────

interface BadgeProps {
  year: string;
  title: string;
  sub: string;
  color: string;
}

const AwardBadge: React.FC<BadgeProps> = ({ year, title, sub, color }) => (
  <div className="integration-logo award-logo" style={{ margin: "8px 0" }}>
    <div style={{
      background: "linear-gradient(160deg, #1a2a4a 0%, #0f1e38 100%)",
      border: "1px solid rgba(255,255,255,0.08)",
      borderRadius: "12px",
      padding: "0",
      width: "160px",
      overflow: "hidden",
      boxShadow: "0 8px 24px rgba(0,0,0,0.3)",
    }}>
      {/* Header bar */}
      <div style={{
        background: "rgba(255,255,255,0.05)",
        borderBottom: "1px solid rgba(255,255,255,0.07)",
        padding: "8px 12px",
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <span style={{ fontSize: "10px", fontWeight: 700, color: "rgba(255,255,255,0.5)", letterSpacing: "0.05em" }}>{year}</span>
        {/* TrustEngine mini logo mark */}
        <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
          <path d="M8 1L2 4v4c0 3.3 2.5 6.4 6 7.2C11.5 14.4 14 11.3 14 8V4L8 1z" fill={color} opacity="0.9"/>
          <path d="M5.5 8l2 2 3-3" stroke="white" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
        </svg>
      </div>

      {/* Badge body */}
      <div style={{ padding: "16px 12px 8px", textAlign: "center" }}>
        <div style={{ fontSize: "16px", fontWeight: 800, color: "#ffffff", lineHeight: 1.25, marginBottom: "6px" }}>
          {title}
        </div>
        <div style={{ fontSize: "9px", fontWeight: 700, color: "rgba(255,255,255,0.4)", letterSpacing: "0.1em", textTransform: "uppercase" }}>
          {sub}
        </div>
      </div>

      {/* Chevron / ribbon bottom */}
      <div style={{ position: "relative", height: "28px", overflow: "hidden" }}>
        <svg viewBox="0 0 160 28" fill="none" style={{ width: "100%", height: "100%" }} preserveAspectRatio="none">
          <path d="M0 0h160v18L80 28 0 18V0z" fill={color} opacity="0.85"/>
        </svg>
      </div>
    </div>
  </div>
);

// ── Badge data ────────────────────────────────────────────────────────────────

const col1: BadgeProps[] = [
  { year: "2025", title: "ISO 27001",         sub: "Certified",          color: "#6366f1" },
  { year: "2025", title: "OJK eKYC",          sub: "Compliant",          color: "#00c896" },
  { year: "2025", title: "SOC 2 Type II",     sub: "Audited",            color: "#8b5cf6" },
  { year: "2025", title: "PPATK AML",         sub: "Ready",              color: "#f59e0b" },
  { year: "2025", title: "ISO 27001",         sub: "Certified",          color: "#6366f1" },
  { year: "2025", title: "OJK eKYC",          sub: "Compliant",          color: "#00c896" },
];

const col2: BadgeProps[] = [
  { year: "2025", title: "BI SNAP",           sub: "Aligned",            color: "#3b82f6" },
  { year: "2025", title: "PDPA / Privacy",    sub: "Act Compliant",      color: "#ec4899" },
  { year: "2025", title: "99.9% Uptime",      sub: "Enterprise SLA",     color: "#10b981" },
  { year: "2025", title: "ISO 9001",          sub: "Quality Mgmt",       color: "#06b6d4" },
  { year: "2025", title: "BI SNAP",           sub: "Aligned",            color: "#3b82f6" },
  { year: "2025", title: "PDPA / Privacy",    sub: "Act Compliant",      color: "#ec4899" },
];

const col3: BadgeProps[] = [
  { year: "2025", title: "Face Match",        sub: "99.8% Accuracy",     color: "#00c896" },
  { year: "2025", title: "Zero Downtime",     sub: "Deployment",         color: "#a855f7" },
  { year: "2025", title: "GDPR Ready",        sub: "Data Residency",     color: "#f97316" },
  { year: "2025", title: "API Security",      sub: "Enterprise Grade",   color: "#0ea5e9" },
  { year: "2025", title: "Face Match",        sub: "99.8% Accuracy",     color: "#00c896" },
  { year: "2025", title: "Zero Downtime",     sub: "Deployment",         color: "#a855f7" },
];

// ── Arrow SVG ─────────────────────────────────────────────────────────────────

const ArrowSVG = () => (
  <svg width="28" height="28" viewBox="0 0 28 28" fill="none">
    <rect x="5.07" y="19.98" width="20.57" height="2.61" transform="rotate(-40.28 5.07 19.98)" fill="white"/>
    <rect x="7.97" y="7.24" width="2.61" height="2.61" transform="rotate(-40.28 7.97 7.24)" fill="white"/>
    <rect x="11.65" y="7.55" width="2.61" height="2.61" transform="rotate(-40.28 11.65 7.55)" fill="white"/>
    <rect x="15.33" y="7.85" width="2.61" height="2.61" transform="rotate(-40.28 15.33 7.85)" fill="white"/>
    <rect x="18.71" y="11.84" width="2.61" height="2.61" transform="rotate(-40.28 18.71 11.84)" fill="white"/>
    <rect x="18.41" y="15.52" width="2.61" height="2.61" transform="rotate(-40.28 18.41 15.52)" fill="white"/>
    <rect x="18.10" y="19.20" width="2.61" height="2.61" transform="rotate(-40.28 18.10 19.20)" fill="white"/>
  </svg>
);

// ── Section ───────────────────────────────────────────────────────────────────

const AwardSection: React.FC = () => {
  return (
    <section className="award pos-rel">
      <div className="container">
        <div className="row align-items-center">

          {/* LEFT */}
          <div className="col-lg-6">
            <div className="sec-title mb-30">
              <span className="sub-title mb-20">Certifications & Compliance</span>
              <h2 className="title title-line_height mb-5">
                Verified by regulators. Trusted by enterprises.
              </h2>
              <p className="content">
                TrustEngine is built to meet the strictest regulatory requirements
                in Indonesia and globally — carrying certifications that demonstrate
                our commitment to data security, privacy, and AML compliance.
              </p>

              <div className="team-btn mt-40">
                <Link className="thm-btn agency-btn" to="/contact">
                  <span className="text">Get compliance docs</span>
                  <span className="arrow">
                    <span className="arrow-icon">
                      <ArrowSVG /><ArrowSVG />
                    </span>
                  </span>
                </Link>
              </div>
            </div>
          </div>

          {/* RIGHT — 3 vertical scrolling columns */}
          <div className="col-lg-6">
            <div className="integration-logo-wrap ul_li">

              <div className="award-logo-inner marquee-first">
                {col1.map((b, i) => <AwardBadge key={i} {...b} />)}
              </div>

              <div className="award-logo-inner marquee2">
                {col2.map((b, i) => <AwardBadge key={i} {...b} />)}
              </div>

              <div className="award-logo-inner marquee-first">
                {col3.map((b, i) => <AwardBadge key={i} {...b} />)}
              </div>

              <div className="xb-shape">
                <div className="shape shape--1"></div>
                <div className="shape shape--2"></div>
              </div>
            </div>
          </div>

        </div>
      </div>

      <span className="award-gradiant"></span>
    </section>
  );
};

export default AwardSection;
